/**
 * SubscriptionGate.jsx
 * Composant qui bloque l'accès si l'abonnement offline est expiré.
 * Affiché à la place de l'app quand :
 *  - On est offline ET
 *  - Le token offline a expiré (> 10 jours sans connexion)
 */

import React, { useEffect, useState } from 'react';
import { WifiOff, RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import { checkSubscriptionOffline } from '../services/subscriptionService';

export default function SubscriptionGate({ children }) {
  const [status, setStatus] = useState(null); // null = loading
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    checkSubscriptionOffline().then(setStatus);

    const onOnline  = () => { setIsOnline(true);  checkSubscriptionOffline().then(setStatus); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Chargement
  if (!status) return children; // Laisser passer pendant le check initial

  // Si online → laisser l'AuthContext gérer (login normal)
  if (isOnline) return children;

  // Si pas de token JWT du tout → page login (pas de blocage SubscriptionGate)
  if (!localStorage.getItem('token')) return children;

  // Valide offline avec token OK
  if (status.valid) return children;

  // Bloqué — token expiré et offline
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center">

        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${
          status.reason === 'subscription_expired'
            ? 'bg-red-100 dark:bg-red-900/30'
            : 'bg-amber-100 dark:bg-amber-900/30'
        }`}>
          {status.reason === 'subscription_expired'
            ? <AlertTriangle size={30} className="text-red-500" />
            : <Clock size={30} className="text-amber-500" />
          }
        </div>

        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
          {status.reason === 'subscription_expired'
            ? 'Abonnement expiré'
            : 'Reconnexion requise'
          }
        </h2>

        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {status.reason === 'subscription_expired'
            ? 'Votre abonnement EasyFacture a expiré. Contactez EasyClick Solution pour le renouveler.'
            : status.reason === 'offline_expired'
            ? `Vous êtes en mode hors-ligne depuis plus de 10 jours. Connectez-vous à internet pour vérifier votre abonnement et continuer à utiliser l'application.`
            : 'Veuillez vous connecter à internet pour continuer.'
          }
        </p>

        {/* Statut connexion */}
        <div className={`flex items-center justify-center gap-2 p-3 rounded-xl mb-5 text-sm font-medium ${
          isOnline
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700'
            : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
        }`}>
          {isOnline
            ? <><RefreshCw size={15} className="animate-spin" /> Connexion détectée…</>
            : <><WifiOff size={15} /> Pas de connexion internet</>
          }
        </div>

        {status.reason !== 'subscription_expired' && (
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition text-sm"
          >
            Vérifier la connexion
          </button>
        )}

        {status.reason === 'subscription_expired' && (
          <a
            href="mailto:contact@easyclick-solution.fr"
            className="w-full block py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition text-sm"
          >
            Contacter EasyClick Solution
          </a>
        )}

        <p className="text-xs text-slate-400 mt-4">
          EasyFacture by EasyClick Solution
        </p>
      </div>
    </div>
  );
}